import { useId, useMemo, useRef, useState } from "react";
import { formatMention, replaceMentions, type User } from "@lumika/shared";
import { cn, Textarea } from "@lumika/ui";
import { userLabel } from "./MentionedText";

/**
 * A teammate the operator inserted via the @-picker. `label` is the
 * display name shown in the textarea (`@<label>`); `id` is the user id
 * the token serializes to.
 */
export interface TrackedMention {
  id: string;
  label: string;
}

/**
 * Convert the operator-facing display text (`@Jane Doe`) into the
 * storage form (`@[<uuid>]`). Only **tracked** mentions are serialized,
 * and only if their `@<label>` is still literally present — so editing
 * inside an inserted name cleanly un-mentions that teammate (the
 * "Mentioning:" list shows the operator exactly what will fire).
 * Longest labels first so `@Jane Doe` is consumed before `@Jane`.
 */
export function serializeMentions(
  displayText: string,
  mentions: TrackedMention[],
): string {
  let out = displayText;
  const byLengthDesc = [...mentions].sort(
    (a, b) => b.label.length - a.label.length,
  );
  for (const m of byLengthDesc) {
    out = out.split(`@${m.label}`).join(formatMention(m.id));
  }
  return out;
}

/**
 * Inverse of `serializeMentions`: turn stored `@[id]` tokens back into
 * editable `@<label>` display text and rebuild the tracked-mention list.
 * Used when an existing mention-bearing body is loaded into an editor
 * (e.g. the task edit dialog). `resolve` returns a teammate's display
 * name, or undefined if the id no longer resolves.
 */
export function hydrateMentions(
  stored: string | null | undefined,
  resolve: (id: string) => string | undefined,
): { text: string; mentions: TrackedMention[] } {
  if (!stored) return { text: "", mentions: [] };
  const mentions: TrackedMention[] = [];
  const text = replaceMentions(stored, (id) => {
    const name = resolve(id);
    if (!name) return "@removed teammate";
    if (!mentions.some((m) => m.id === id)) mentions.push({ id, label: name });
    return `@${name}`;
  });
  return { text, mentions };
}

/** The mentions whose `@<label>` is still present in the current text. */
function activeMentions(
  displayText: string,
  mentions: TrackedMention[],
): TrackedMention[] {
  return mentions.filter((m) => displayText.includes(`@${m.label}`));
}

interface ActiveQuery {
  /** Text typed after the `@`, used to filter the picker. */
  text: string;
  /** Index of the `@` in the value. */
  start: number;
}

/**
 * Find an in-progress `@mention` immediately before the caret: an `@`
 * at the start of a word with only non-whitespace between it and the
 * caret. Returns null when the caret is not inside a mention.
 */
function detectQuery(value: string, caret: number): ActiveQuery | null {
  for (let i = caret - 1; i >= 0; i -= 1) {
    const ch = value[i];
    if (ch === " " || ch === "\n" || ch === "\t") return null;
    if (ch === "@") {
      const before = i > 0 ? value[i - 1] : " ";
      const atWordStart =
        i === 0 || before === " " || before === "\n" || before === "\t";
      return atWordStart ? { text: value.slice(i + 1, caret), start: i } : null;
    }
  }
  return null;
}

const MAX_SUGGESTIONS = 6;

/**
 * Internal-note composer with an @-mention picker. A plain controlled
 * `<textarea>` (no rich-text dependency): the operator sees readable
 * `@Display Name` text; `serializeMentions` turns it into `@[id]` tokens
 * at submit time. The live "Mentioning:" row makes the fan-out visible.
 */
export function MentionTextarea({
  value,
  onChange,
  mentions,
  onMentionsChange,
  teammates,
  onSubmit,
  placeholder,
  rows = 2,
  disabled,
  className,
  autoGrow = false,
  allowAi = false,
}: {
  value: string;
  onChange: (next: string) => void;
  mentions: TrackedMention[];
  onMentionsChange: (next: TrackedMention[]) => void;
  teammates: User[];
  /** Called on Enter (no modifier) while the picker is closed. */
  onSubmit?: () => void;
  placeholder?: string;
  rows?: number;
  disabled?: boolean;
  className?: string;
  /** Grow to fit content (Telegram-style); see Textarea's autoGrow. */
  autoGrow?: boolean;
  /**
   * Offer the dealership's AI user in the picker. Enabled only where
   * mentioning the AI actually does something — the lead
   * internal-note composer, where an @AI mention raises an AI
   * directive. Everywhere else a mention of the AI is inert, so it is
   * left out of the picker to avoid a confusing dead end.
   */
  allowAi?: boolean;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [query, setQuery] = useState<ActiveQuery | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  // Stable ids tying the textarea to the suggestion list, so assistive
  // tech can announce that a picker opened and which option is active.
  const listboxId = useId();

  // Picker candidates: active teammates. The AI user is offered only
  // where `allowAi` is set — mentioning it there raises an AI directive.
  // System users are never offered: a mention would ping Lumika staff and
  // put their name into note text the AI may read.
  const candidates = useMemo(
    () =>
      teammates.filter(
        (u) =>
          u.status === "active" &&
          !u.isSystemUser &&
          (u.kind === "human" || (allowAi && u.kind === "ai")),
      ),
    [teammates, allowAi],
  );

  const matches = useMemo(() => {
    if (!query) return [];
    const needle = query.text.toLowerCase();
    return candidates
      .filter((u) => userLabel(u).toLowerCase().includes(needle))
      .slice(0, MAX_SUGGESTIONS);
  }, [candidates, query]);

  const shown = activeMentions(value, mentions);

  function refreshQuery(el: HTMLTextAreaElement) {
    const caret = el.selectionStart ?? el.value.length;
    setQuery(detectQuery(el.value, caret));
    setActiveIndex(0);
  }

  function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const next = e.target.value;
    onChange(next);
    // Reconcile tracked mentions — a mention whose `@label` was edited
    // away is dropped, so it visibly leaves the "Mentioning:" row.
    const surviving = mentions.filter((m) => next.includes(`@${m.label}`));
    if (surviving.length !== mentions.length) onMentionsChange(surviving);
    refreshQuery(e.target);
  }

  function pick(user: User) {
    if (!query) return;
    const base = userLabel(user);
    // Two teammates can share a display name; `serializeMentions` maps
    // labels to ids by string match, so duplicate labels would all
    // serialize to one id. Suffix the email to keep each label unique.
    const collides = candidates.some(
      (u) => u.id !== user.id && userLabel(u) === base,
    );
    const label =
      collides && user.email?.trim() ? `${base} (${user.email.trim()})` : base;
    const token = `@${label}`;
    const before = value.slice(0, query.start);
    const after = value.slice(query.start + 1 + query.text.length);
    const next = `${before}${token} ${after}`;
    onChange(next);
    if (!mentions.some((m) => m.id === user.id && m.label === label)) {
      onMentionsChange([...mentions, { id: user.id, label }]);
    }
    setQuery(null);
    const caret = before.length + token.length + 1;
    requestAnimationFrame(() => {
      const el = ref.current;
      if (el) {
        el.focus();
        el.setSelectionRange(caret, caret);
      }
    });
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (query && matches.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % matches.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => (i - 1 + matches.length) % matches.length);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        const choice = matches[activeIndex] ?? matches[0];
        if (choice) pick(choice);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setQuery(null);
        return;
      }
    }
    // Picker closed: Cmd/Ctrl+Enter submits; plain Enter keeps its
    // default newline so a half-typed note can't post by accident.
    // Without a submit handler (the dialog description/body fields)
    // Enter always stays a newline.
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && onSubmit) {
      e.preventDefault();
      onSubmit();
    }
  }

  return (
    // `flex-1 min-w-0` makes this wrapper fill the remaining
    // space in a flex-row parent (the lead page's note
    // composer puts the textarea + a Send button on one row, and
    // we want the textarea to grow to match the reply composer's
    // width). `flex-1` is a no-op outside flex contexts so the
    // FormField usages (TaskDialogs, Contact360 follow-up dialog)
    // keep their block layout. `min-w-0` keeps the textarea from
    // pushing past the row's edge when long lines wrap.
    <div className="relative flex flex-1 min-w-0 flex-col gap-1">
      <Textarea
        ref={ref}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onClick={(e) => refreshQuery(e.currentTarget)}
        // Delay so a mousedown on a suggestion still registers.
        onBlur={() => window.setTimeout(() => setQuery(null), 150)}
        aria-autocomplete="list"
        // aria-expanded is unsupported on the textbox role; haspopup is.
        aria-haspopup="listbox"
        aria-controls={query && matches.length > 0 ? listboxId : undefined}
        aria-activedescendant={
          query && matches.length > 0
            ? `${listboxId}-opt-${activeIndex}`
            : undefined
        }
        rows={rows}
        autoGrow={autoGrow}
        placeholder={placeholder}
        disabled={disabled}
        className={cn("resize-none", className)}
      />

      {query && matches.length > 0 ? (
        <div
          id={listboxId}
          role="listbox"
          className="absolute bottom-full left-0 z-50 mb-1 w-72 overflow-hidden rounded-lg border bg-card shadow-md"
        >
          {matches.map((u, i) => (
            <button
              key={u.id}
              id={`${listboxId}-opt-${i}`}
              type="button"
              role="option"
              aria-selected={i === activeIndex}
              // Keep textarea focus so the click lands without a blur race.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(u)}
              onMouseEnter={() => setActiveIndex(i)}
              className={cn(
                "flex w-full flex-col px-3 py-1.5 text-left",
                i === activeIndex ? "bg-primary/10" : "bg-card",
              )}
            >
              <span className="text-sm font-medium text-foreground">
                {userLabel(u)}
              </span>
              {u.jobTitle ? (
                <span className="text-xs text-muted-foreground">
                  {u.jobTitle}
                </span>
              ) : null}
            </button>
          ))}
        </div>
      ) : null}

      {shown.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
          <span>Mentioning:</span>
          {shown.map((m) => (
            <span
              key={`${m.id}-${m.label}`}
              className="rounded bg-primary/10 px-1 font-medium text-primary"
            >
              @{m.label}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
